/**
 * Stable per-browser identifier, used by the backend to enforce one active
 * web session per subscriber account (independent of the mobile app's own
 * one-session limit — see epaper-session.js on the backend).
 *
 * Persisted to localStorage rather than sessionStorage so it survives tab
 * closes and logout, meaning logging back in on the same browser reuses the
 * same session row instead of kicking yourself.
 */

const DEVICE_ID_KEY = "gs_web_device_id";

let cachedDeviceId: string | null = null;

const uuidv4 = (): string =>
  "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
    const r = Math.floor(Math.random() * 16);
    const v = c === "x" ? r : (r % 4) + 8;
    return v.toString(16);
  });

export const getDeviceId = (): string => {
  if (cachedDeviceId) return cachedDeviceId;

  try {
    const stored = window.localStorage.getItem(DEVICE_ID_KEY);
    if (stored) {
      cachedDeviceId = stored;
      return stored;
    }
  } catch {
    // localStorage unavailable (private mode, SSR, etc.) — fall through and
    // mint a volatile id so login still works.
  }

  const fresh = uuidv4();
  cachedDeviceId = fresh;
  try {
    window.localStorage.setItem(DEVICE_ID_KEY, fresh);
  } catch {
    // non-fatal
  }
  return fresh;
};

/** Device metadata sent alongside credentials so sessions are identifiable. */
export const getDeviceInfo = () => ({
  device_id: getDeviceId(),
  device_name: typeof navigator !== "undefined" ? navigator.userAgent : "Web",
  platform: "web",
});
